import {
    MAX_ROW_LIMIT,
    buildResource,
    fetchDailySeries,
    fetchRowChunk,
    runQueries,
} from '../GscTools/query';
import { groupFilters } from './groups';

/**
 * The requests this report makes, one function per block.
 *
 * Every one of them is a page-filtered ask: a group's rules go into the request as its "and"
 * filter group, alongside the scope's own device and country, and nothing is ever derived by
 * subtracting one group from another or from an unfiltered total. A group is only ever its own
 * response.
 *
 * Aggregation is by page, which is what a page filter means: each page counts its own clicks and
 * impressions, so a result showing two pages of a group counts twice in its impressions. That is
 * the honest reading of "how did these pages do", and it is also why a group's totals are never
 * comparable with the property's own by-property figures.
 */

const AGGREGATION = 'byPage';

/** The parts of the scope every request on this report carries, besides the group's filters. */
function scopeRequest(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
    };
}

/**
 * Block 02 — one group's totals for each window, asked for with no dimension at all.
 *
 * One call per group, holding both of its windows, so groups fail independently: a group that
 * errors leaves the others' rows on screen. The baseline is left out when there is no comparison
 * rather than sent and ignored. Groups are only ever the ones the session defined — there is no
 * fan-out to anything the user did not name.
 */
export async function fetchGroupTotals({ credentialsDomainId, scope, group }) {
    const filters = groupFilters(group);

    const queries = Object.entries({ current: scope.current, baseline: scope.baseline })
        .filter(([, period]) => !!period)
        .map(([key, period]) => ({
            key,
            resource: buildResource({
                period,
                dimensions: [],
                aggregationType: AGGREGATION,
                ...scopeRequest(scope),
                filters,
            }),
        }));

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries,
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}

/**
 * Block 03 — the active group's daily rows for one window.
 *
 * Each window is its own call with the identical rule and scope, so a baseline that fails leaves
 * the current line drawn. A day Search Console returned nothing for stays a gap downstream; it is
 * never filled in from the neighbouring days or from the other window.
 */
export function fetchGroupDaily({ credentialsDomainId, scope, group, period }) {
    return fetchDailySeries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        ...scopeRequest(scope),
        filters: groupFilters(group),
        aggregationType: AGGREGATION,
    });
}

/**
 * Block 04 — one chunk of the active group's matching pages, for the detail window.
 *
 * Paged through the shared bounded retrieval, so "load more" asks for the next `startRow` and
 * stops at the product cap. What comes back is the pages Search Console was willing to return
 * under this rule — never an inventory of the group's URLs.
 */
export function fetchGroupPages({ credentialsDomainId, scope, group, period, offset = 0 }) {
    return fetchRowChunk({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        dimension: 'page',
        ...scopeRequest(scope),
        filters: groupFilters(group),
        offset,
    });
}
